import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Accordion, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import ReactSelect from 'react-select'
import { discussionModelsGetAll } from '../../../api'
import { TrashIconRow /*trashIconString*/ } from '../../../common/icons'
import { PersonSelect } from '../../../common/personselect'
import { PersonSelectMulti } from '../../../common/personselectmulti'
import { jsError, localDate, localized } from '../../../common/utils'
import { actionTypeSelector, authSelector } from '../../../reducers'
import 'ag-grid-enterprise'

import { getLocaleText } from '../../../actions/datagrid'
import { Button } from '../../../common/Button.tsx'
import DefaultDatePicker from '../../../common/datepicker/DefaultDatePicker.jsx'
import { getRowId } from '../../../common/grid'
import { reactSelectSearchSettings } from '../../../common/reactselect'

const newQA = () => ({ question: '', answer: '', idx: Math.random() })
const newEvaluation = () => ({ id: Math.random(), question: '', grade: 0 })
const getIdx = (target) => Number(target.parentElement.parentElement.parentElement.dataset.idx)
let startEditing = false

export function DiscussionGroup1({ state, handleChange, setDiscussion }) {
	const { t } = useTranslation()
	const auth = useSelector(authSelector)
	const prefix = 'discussionModal'
	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	const statusMap = {
		OPEN: t('Open', 'Avoinna'),
		DONE: t('Done', 'Pidetty'),
		LATE: t('Late', 'Myöhässä'),
	}

	const initialQas = state.personId > 0 ? [...state.qas] : [...state.qas, newQA()]
	const [qas, setQas] = useState(initialQas)

	const [evaluationRows, setEvaluationRows] = useState(state?.evaluations ?? [])
	useEffect(() => {
		setEvaluationRows(state?.evaluations ?? [])
	}, [state.evaluations])

	const updateEvaluations = () => setDiscussion((data) => ({ ...data, evaluations: evaluationRows }))

	const updateGrades = (rowIndex, value) =>
		setEvaluationRows((rows) => {
			const newRows = rows.map((evaluation, idx) => (idx === rowIndex ? { ...evaluation, grade: value } : evaluation))
			setDiscussion((data) => ({ ...data, evaluations: newRows }))
			return newRows
		})

	const deleteEvaluation = useCallback(
		(id) => {
			setEvaluationRows((rows) => {
				const filteredRows = rows.filter((row) => Number(row.id) !== Number(id))
				setDiscussion((data) => ({ ...data, evaluations: filteredRows }))
				return filteredRows
			})
		},
		[setDiscussion],
	)

	const questionChange = ({ target }) => {
		const idx = getIdx(target)

		const newQas = [...qas]
		newQas[idx].question = target.value
		// insert a new question-answer pair when the last question is modified
		if (idx === newQas.length - 1 && target.value.trim().length > 0) {
			newQas.push(newQA())
		}
		setQas(newQas)

		handleChange({
			target: {
				id: 'qas',
				value: newQas,
			},
		})
	}

	const answerChange = ({ target }) => {
		const idx = getIdx(target)

		const newQas = qas.map((qa, qaIdx) => (idx === qaIdx ? { ...qa, answer: target.value } : qa))

		setQas(newQas)

		handleChange({
			target: {
				id: 'qas',
				value: newQas,
			},
		})
	}

	const [discussionModel, setDiscussionModel] = useState([])
	const [discussionModels, setDiscussionModels] = useState([])
	const [discussionModelOptions, setDiscussionModelOptions] = useState([])

	const fetchDiscussionModels = useCallback(async () => {
		const res = await discussionModelsGetAll()
		if (res.status === 200) {
			setDiscussionModels(res.data)
			setDiscussionModelOptions(
				res.data.map((model) => ({
					value: model.id,
					label: `${model.discussionModelName} (${model.discussionPrintLanguage})${model.modelValidUntil ? ` [${model.modelValidUntil}]` : ''}`,
					isDisabled: model.modelValidUntil < localDate(),
				})),
			)
		} else {
			jsError(res)
		}
	}, [])

	useEffect(() => {
		if (!isPersonal) {
			fetchDiscussionModels()
		}
	}, [isPersonal, fetchDiscussionModels])

	const selectModel = (newValue, actionMeta) => {
		const selectedModel = discussionModels.find((model) => model.id === Number(newValue.value))
		setQas([...selectedModel.qas])
		setDiscussionModel(selectedModel)
		setDiscussion((data) => {
			// sets discussionModel.id
			const newData = {
				...data,
				...selectedModel, // insert selected model data as default
				[actionMeta.name]: { id: newValue.value },
			}
			return newData
		})
	}

	const [person, setPerson] = useState(state)
	const selectPerson = (newValue, actionMeta) => {
		setPerson({ ...state, [actionMeta.name]: newValue.value })
		const discussionQas = discussionModel.qas.map((qa, idx) => ({ question: qa.question, answer: '', idx }))
		setDiscussion((data) => {
			// sets personId and sets discussion.id to zero (new discussion)
			const newData = {
				...data,
				discussionStatus: 'OPEN',
				qas: discussionQas,
				discussionModel: { ...discussionModel },
				[actionMeta.name]: newValue.value,
				id: 0,
			}
			return newData
		})
		setQas(discussionQas)
	}

	const selectParticipants = (newValue, actionMeta) =>
		setDiscussion((data) => ({
			...data,
			[actionMeta.name]: newValue.map((person) => ({ personId: person.value })),
		}))

	const [createNewDiscussion, setCreateNewDiscussion] = useState(false)

	const actionType = useSelector(actionTypeSelector)
	useEffect(() => {
		switch (actionType) {
			case 'POST_DISCUSSION_MODEL_SUCCEEDED':
			case 'POST_DISCUSSION_MODEL_EDIT_SUCCEEDED':
				fetchDiscussionModels()
				setCreateNewDiscussion(false)
				setQas(initialQas)
				break
		}
	}, [actionType, initialQas, fetchDiscussionModels])

	const gridRef = useRef()

	const dataRef = useRef()
	dataRef.current = state

	const EvaluationGradeRenderer = (params) => {
		return (
			<input
				type="radio"
				onChange={() => updateGrades(params.node.rowIndex, Number(params.colDef.headerName))}
				name={params.node.rowIndex}
				defaultChecked={Number(params.colDef.headerName) === params.data.grade}
				disabled={dataRef.current.discussionStatus !== 'OPEN' && dataRef.current.discussionStatus !== 'LATE'}
			/>
		)
	}

	const EvaluationDeleteRenderer = (params) => {
		return (
			<div>
				<Button onClick={() => deleteEvaluation(params.node.data.id)} className="btn deleteRowBtn btn-lg">
					<TrashIconRow />
				</Button>
			</div>
		)
	}

	const gridComponents = {
		evaluationGradeRenderer: EvaluationGradeRenderer,
		evaluationDeleteRenderer: EvaluationDeleteRenderer,
	}

	const columnDefs = [
		{
			headerName: '',
			field: 'delete',
			cellRenderer: 'evaluationDeleteRenderer',
			width: 100,
			suppressHeaderMenuButton: true,
			hide: !((createNewDiscussion || state.personId) < 1 && state.discussionStatus !== 'MODEL'),
		},
		{
			headerName: t('Evaluation', 'Arviointi'),
			field: 'question',
			editable: !!((createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL'),
			width: 500,
			autoHeight: true,
		},
		{
			headerName: '1',
			field: 'grade',
			width: 75,
			suppressHeaderMenuButton: true,
			cellRenderer: 'evaluationGradeRenderer',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: '2',
			field: 'grade',
			width: 75,
			suppressHeaderMenuButton: true,
			cellRenderer: 'evaluationGradeRenderer',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: '3',
			field: 'grade',
			width: 75,
			suppressHeaderMenuButton: true,
			cellRenderer: 'evaluationGradeRenderer',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: '4',
			field: 'grade',
			width: 75,
			suppressHeaderMenuButton: true,
			cellRenderer: 'evaluationGradeRenderer',
			filter: 'agSetColumnFilter',
		},
		{
			headerName: '5',
			field: 'grade',
			width: 75,
			suppressHeaderMenuButton: true,
			cellRenderer: 'evaluationGradeRenderer',
			filter: 'agSetColumnFilter',
		},
	]

	useEffect(() => {
		if (startEditing) {
			setTimeout(() => {
				gridRef.current.api.startEditingCell({
					rowIndex: evaluationRows.length - 1,
					colKey: 'question',
				})
				startEditing = false
			}, 100)
		}
	}, [evaluationRows])
	/*
	const getContextMenuItems = useCallback((params) => {
		const items = []
		if (!params.api.isPivotMode()) {
			items.push({
				name: t('DeleteEvaluation', 'Poista arviointi'),
				action: () => deleteEvaluation(params.node.data.id),
				icon: trashIconString,
			})
			return items
		}
	}, [t, deleteEvaluation])
*/
	return (
		<Accordion defaultActiveKey={['discussion-1', 'discussion-2']} flush alwaysOpen>
			<Accordion.Item key="discussion-1" eventKey="discussion-1">
				<Accordion.Header>{t('DiscussionInfo', 'Keskustelun tiedot')}</Accordion.Header>
				<Accordion.Body>
					{((!createNewDiscussion && state.discussionModel?.id === 0) || state.personId > 0) && (
						<Row>
							<Form.Group as={Col} xl={4} controlId="discussionModel">
								<Form.Label>{t('DiscussionModel', 'Keskustelumalli')}</Form.Label>
								<ReactSelect
									name="discussionModel"
									inputId="discussionModel"
									onChange={selectModel}
									isDisabled={state.personId > 0}
									options={discussionModelOptions}
									value={discussionModelOptions.find((option) => option.value === state.discussionModel?.id)}
									{...reactSelectSearchSettings(t)}
								/>
							</Form.Group>
							{state.personId < 1 && auth.discussionsAdmin && (
								<Form.Group as={Col} xl={3} className="mt-4" controlId="createNewDiscussion">
									<Form.Check
										type="checkbox"
										label={t('CreateNewDiscussion', 'Luo uusi keskustelumalli')}
										checked={createNewDiscussion}
										onChange={() => setCreateNewDiscussion(!createNewDiscussion)}
									/>
								</Form.Group>
							)}
						</Row>
					)}
					{(state.discussionModel?.id > 0 || createNewDiscussion) && state.personId < 1 && (
						<Row>
							<Form.Group as={Col} xl={4} controlId="discussionModelName">
								<Form.Label>{t('DiscussionModelName', 'Keskustelumallin nimi')}</Form.Label>
								<Form.Control
									defaultValue={state.discussionModel?.discussionModelName || discussionModel.discussionModelName}
									onChange={handleChange}
									disabled={state.discussionStatus === 'MODEL'}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={4} controlId="discussionPrintLanguage">
								<Form.Label>{t('DiscussionPrintLanguage', 'Keskustelun tulostuskieli')}</Form.Label>
								<Form.Select
									defaultValue={
										state.discussionModel?.discussionPrintLanguage || discussionModel.discussionPrintLanguage
									}
									onChange={handleChange}
									disabled={state.discussionStatus === 'MODEL'}
								>
									{localized.languageOptions}
								</Form.Select>
							</Form.Group>
							<Form.Group as={Col} className="mt-4" xl={4} controlId="modelValidUntil">
								<DefaultDatePicker
									id={`${prefix}-modelValidUntil`}
									name="modelValidUntil"
									label={t('ModelValidUntil', 'Keskustelumallin voimassaolo päättyy')}
									value={
										state.modelValidUntil ||
										state.discussionModel?.modelValidUntil ||
										discussionModel.modelValidUntil ||
										''
									}
									onChange={(iso) =>
										handleChange({
											target: { id: 'modelValidUntil', value: iso ?? '' },
										})
									}
									disabled={state.discussionStatus === 'MODEL'}
								/>
							</Form.Group>
						</Row>
					)}
					{state.discussionModel?.id > 0 && (
						<Row className="mb-1">
							<PersonSelect
								disabled={state.personId > 0}
								selectedPerson={state.personId > 0 ? state : person}
								selectChange={selectPerson}
								supervised={auth.role === 'SUPERVISOR'}
							/>
						</Row>
					)}
					{state.personId > 0 && (
						<>
							<Row>
								<Form.Group as={Col} xl={4} controlId="discussionStatus">
									<Form.Label>{t('DiscussionStatus', 'Keskustelun tila')}</Form.Label>
									<Form.Select onChange={handleChange} value={state.discussionStatus} disabled={isPersonal}>
										<option value="">{t('Select', 'Valitse')}</option>
										{Object.keys(statusMap).map((status) => (
											<option key={status} value={status}>
												{statusMap[status]}
											</option>
										))}
									</Form.Select>
								</Form.Group>
								<Form.Group as={Col} xl={4} className="mt-4" controlId="discussionDate">
									<DefaultDatePicker
										id={`${prefix}-discussionDate`}
										name="discussionDate"
										label={t('DiscussionDate', 'Päivämäärä')}
										value={state.discussionDate ?? ''}
										onChange={(iso) =>
											handleChange({
												target: { id: 'discussionDate', value: iso ?? '' },
											})
										}
										disabled={isPersonal}
									/>
								</Form.Group>
							</Row>
							<Row>
								<PersonSelectMulti
									selectedPersons={state.participants}
									excludedIds={[state.personId]}
									selectChange={selectParticipants}
									disabled={state.discussionStatus === 'DONE' || isPersonal}
								/>
							</Row>
						</>
					)}
				</Accordion.Body>
			</Accordion.Item>
			{(state.discussionModel?.id > 0 || createNewDiscussion) && (
				<Accordion.Item key="discussion-2" eventKey="discussion-2">
					<Accordion.Header>{t('OpenQuestions', 'Avoimet kysymykset')}</Accordion.Header>
					<Accordion.Body>
						<ol className="discussion-qas">
							{qas.map((qa, idx) => (
								<div key={qa.idx} data-idx={idx}>
									<Row>
										{state.discussionModel?.discussionStatus === 'MODEL' ? (
											<li className="discussion-question">{qa.question}</li>
										) : (
											<Form.Group as={Col} xl={12} controlId={`question${idx}`}>
												{(createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL' && (
													<Form.Label>{`${t('Question', 'Kysymys')} ${idx + 1}.`}</Form.Label>
												)}
												<Form.Control
													value={qa.question}
													onChange={questionChange}
													disabled={
														!((createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL')
													}
													className={
														(createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL'
															? undefined
															: 'ag-evaluation-question-ready'
													}
												/>
											</Form.Group>
										)}
									</Row>
									<Row>
										<Form.Group as={Col} xl={12} controlId={`answer${idx}`}>
											<Form.Label>{`${t('AnswerToQuestion', 'Vastaus kysymykseen')} ${idx + 1}.`}</Form.Label>
											<Form.Control
												as="textarea"
												rows="4"
												value={qa.answer}
												placeholder={state.discussionModel?.qas?.[idx].answer}
												onChange={answerChange}
												disabled={state.discussionStatus === 'MODEL' || state.discussionStatus === 'DONE'}
											/>
										</Form.Group>
									</Row>
								</div>
							))}
						</ol>
						{(!state.discussionStatus || state.discussionStatus === 'NEWMODEL') && (
							<Button
								testId="new-qas"
								variant="primary"
								className="roundAddButton w-5"
								onClick={() => {
									const newQas = [...qas, newQA()]
									setQas(newQas)

									handleChange({
										target: {
											id: 'qas',
											value: newQas,
										},
									})
								}}
							>
								+
							</Button>
						)}
					</Accordion.Body>
				</Accordion.Item>
			)}
			{((state.discussionModel?.id > 0 && state.personId < 1) ||
				(state.discussionModel?.id > 0 && state.personId > 0 && evaluationRows.length > 0) ||
				createNewDiscussion) && (
				<Accordion.Item key="discussion-3" eventKey="discussion-3">
					<Accordion.Header>{t('Evaluation', 'Arviointi')}</Accordion.Header>
					<Accordion.Body>
						<Row>
							<Row>
								<Form.Group as={Col} xl={12} controlId="evaluationInfo">
									{(createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL' && (
										<Form.Label>{t('EvaluationInfo', 'Selite')}</Form.Label>
									)}
									<Form.Control
										onChange={handleChange}
										value={state?.evaluationInfo}
										disabled={!((createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL')}
										className={
											(createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL'
												? undefined
												: 'ag-evaluation-question-ready'
										}
									/>
								</Form.Group>
							</Row>
							{(createNewDiscussion || state.personId < 1) && state.discussionStatus !== 'MODEL' && (
								<Button
									className="roundAddButton w-5"
									onClick={() => {
										setEvaluationRows((oldEvaluationRows) => {
											return [...oldEvaluationRows, newEvaluation()]
										})
										startEditing = true
									}}
								>
									+
								</Button>
							)}
							<Row className="ag-theme-material styles-ag flex-grow-1 h-450">
								<AgGridReact
									ref={gridRef}
									columnDefs={columnDefs}
									rowData={evaluationRows}
									components={gridComponents}
									getRowId={getRowId}
									getLocaleText={(params) => getLocaleText(t, params)}
									//getContextMenuItems={(createNewDiscussion || state.personId < 1) && (state.discussionStatus !== "MODEL") ? getContextMenuItems : []}
									animateRows
									//suppressCellFocus // does not work with editing
									singleClickEdit
									stopEditingWhenCellsLoseFocus
									enableCellTextSelection
									onCellEditingStopped={updateEvaluations}
									//onGridReady={({ api }) => api.sizeColumnsToFit()}
									debug={import.meta.env.DEV}
								/>
							</Row>
						</Row>
					</Accordion.Body>
				</Accordion.Item>
			)}
		</Accordion>
	)
}
